import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WINDOW } from 'src/app/core/core.module';
import { ContractRequestFilter } from '../models/contract-request-filter.model';
import { DuplicateContractRequestKey } from '../enum/duplicate-contract-request-key.enum';
import { ReviewAndSendReason } from '../models/review-and-send-reason.model';
import { MarginCalculatorMarginService } from '../services/margin-calculator-margin.service';
import { ValidationRuleInvocation } from 'src/app/shared/lib/validations/models/validation-rule-invocation.model';
import { ValidationRuleEnum } from 'src/app/shared/lib/validations/models/validation-rule.enum';
import dayjs from 'dayjs';
import { RuleDuplicateContractRequest } from 'src/app/shared/lib/validations/models/rules/duplicate-contract-request.model';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';

@UntilDestroy()
@Component({
    selector: 'existing-contract-request-dialog',
    templateUrl: './existing-contract-request.dialog.component.html',
    styleUrls: ['./dialog-styles.scss', './existing-contract-request.dialog.component.scss']
})
export class ExistingContractRequestDialog implements OnInit {
    isLoading = false;
    softStopForExistingContractRequest = false;
    contractRequestFilter: ContractRequestFilter;
    selectedReason: string;

    reasons: ReviewAndSendReason[] = [
        {
            key: DuplicateContractRequestKey.ReviewAndSend,
            name: 'This request is for a different contract than one that currently exists'
        },
        {
            key: DuplicateContractRequestKey.ViewExistingRequest,
            name: 'This request is to adjust/correct/add to one that currently exists'
        }
    ];

    constructor(
        private readonly dialogRef: MatDialogRef<ExistingContractRequestDialog>,
        private readonly marginService: MarginCalculatorMarginService,
        private readonly _featureManager: LDFeatureManager,
        @Inject(WINDOW) private readonly window: Window,
        @Inject(MAT_DIALOG_DATA)
        public data: { candidateName: string; facility: string; contractRequestCount: number; marginId: number }
    ) {}

    ngOnInit(): void {
        this.contractRequestFilter = this.data;
        this._featureManager
            .isEnabled(FeatureFlag.SoftStopForExistingContractRequest)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                this.softStopForExistingContractRequest = flagIsEnabled;
            });
    }

    public close(): void {
        this.dialogRef.close(null);
    }

    public continueToReviewAndSend(): void {
        const payload = this.createvalidationRuleInvocationObject(
            DuplicateContractRequestKey.ReviewAndSend,
            false,
            true
        );
        this.marginService
            .saveMarginRuleValidation(this.data.marginId, [payload])
            .subscribe({ error: () => console.error(`Validation messed up for ${this.data.marginId}`) });
        this.dialogRef.close(DuplicateContractRequestKey.ReviewAndSend);
    }

    public viewExistingContractRequests(): void {
        this.dialogRef.close(DuplicateContractRequestKey.ViewExistingRequest);
        const payload = this.createvalidationRuleInvocationObject(
            DuplicateContractRequestKey.ViewExistingRequest,
            this.softStopForExistingContractRequest ? false : true,
            false
        );
        this.marginService
            .saveMarginRuleValidation(this.data.marginId, [payload])
            .subscribe({ error: () => console.error(`Validation messed up for ${this.data.marginId}`) });
        localStorage.setItem('margincalc.contractrequest.filter', JSON.stringify(this.contractRequestFilter));
        this.window.open(`#/recruiting/contract-requests-new`, '_blank');
    }

    private createvalidationRuleInvocationObject(
        reason: string,
        softOrHardStop: boolean,
        succeededValidation: boolean
    ): ValidationRuleInvocation {
        const data: RuleDuplicateContractRequest = {
            CandidateName: this.data.candidateName,
            FacilityName: this.data.facility,
            ContractRequestCount: this.data.contractRequestCount,
            Reason: reason
        };

        return {
            data,
            validationType: ValidationRuleEnum.ContractRequestDuplicate,
            softOrHardStop,
            shouldEmail: false,
            succeededValidation,
            createdOn: dayjs().format()
        };
    }
}
